/**
 * Global polling registry to track and cleanup all polling intervals
 * This ensures no polling intervals are left running when navigating between pages
 */

class PollingRegistry {
  constructor() {
    this.activePolls = new Map();
    this.setupGlobalCleanup();
  }

  /**
   * Register a new polling interval
   * @param {string} id - Unique identifier for this polling
   * @param {number} intervalId - The interval ID returned by setInterval
   * @param {string} component - Component name that created this polling
   * @param {string} description - Description of what this polling does
   */
  register(id, intervalId, component, description) {
    console.log(`📝 Registering polling: ${id} from ${component}`);
    
    // Clear any existing polling with the same ID
    if (this.activePolls.has(id)) {
      this.cleanup(id);
    }
    
    this.activePolls.set(id, {
      intervalId,
      component,
      description,
      created: new Date(),
      active: true
    });
    
    console.log(`✅ Polling registered: ${id} (Total active: ${this.activePolls.size})`);
  }

  /**
   * Cleanup a specific polling interval
   * @param {string} id - The polling ID to cleanup
   */
  cleanup(id) {
    if (this.activePolls.has(id)) {
      const poll = this.activePolls.get(id);
      console.log(`🧹 Cleaning up polling: ${id} from ${poll.component}`);
      
      if (poll.intervalId) {
        clearInterval(poll.intervalId);
      }
      
      this.activePolls.delete(id);
      console.log(`✅ Polling cleaned up: ${id} (Remaining: ${this.activePolls.size})`);
      return true;
    }
    
    console.log(`⚠️ Polling not found for cleanup: ${id}`);
    return false;
  }

  /**
   * Cleanup all polling intervals from a specific component
   * @param {string} component - Component name
   */
  cleanupByComponent(component) {
    console.log(`🧹 Cleaning up all polling from component: ${component}`);
    let cleaned = 0;
    
    for (const [id, poll] of this.activePolls.entries()) {
      if (poll.component === component) {
        this.cleanup(id);
        cleaned++;
      }
    }
    
    console.log(`✅ Cleaned up ${cleaned} polling intervals from ${component}`);
    return cleaned;
  }

  /**
   * Cleanup all polling intervals (nuclear option)
   */
  cleanupAll() {
    console.log(`🧹 NUCLEAR CLEANUP: Clearing all ${this.activePolls.size} polling intervals`);
    
    for (const [id, poll] of this.activePolls.entries()) {
      console.log(`  - Clearing ${id} from ${poll.component}`);
      if (poll.intervalId) {
        clearInterval(poll.intervalId);
      }
    }
    
    this.activePolls.clear();
    console.log('✅ All polling intervals cleared');
  }

  /**
   * Get all active polling intervals
   */
  getActive() {
    const active = [];
    for (const [id, poll] of this.activePolls.entries()) {
      active.push({ id, ...poll });
    }
    return active;
  }

  /**
   * Log all active polling intervals
   */
  logActive() {
    const active = this.getActive();
    console.log(`🔍 ACTIVE POLLING INTERVALS: ${active.length}`);
    
    if (active.length === 0) {
      console.log('  ✅ No active polling intervals');
      return active;
    }
    
    active.forEach((poll, index) => {
      console.log(`  ${index + 1}. ${poll.id} (${poll.component})`);
      console.log(`     Description: ${poll.description}`);
      console.log(`     Created: ${poll.created.toISOString()}`);
      console.log(`     Duration: ${Math.round((new Date() - poll.created) / 1000)}s`);
    });
    
    return active;
  }

  /**
   * Setup global cleanup mechanisms
   */
  setupGlobalCleanup() {
    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
      console.log('🧹 Page unloading, cleaning up all polling');
      this.cleanupAll();
    });
  }

  /**
   * Check for orphaned intervals (intervals that should have been cleaned up)
   */
  checkForOrphans() {
    const now = new Date();
    const orphans = [];

    for (const [id, poll] of this.activePolls.entries()) {
      const age = now - poll.created;
      // If polling has been running for more than 5 minutes, it might be orphaned
      if (age > 5 * 60 * 1000) {
        orphans.push({ id, ...poll, age });
      }
    }

    if (orphans.length > 0) {
      console.warn(`⚠️ Found ${orphans.length} potentially orphaned polling intervals:`);
      orphans.forEach(orphan => {
        console.warn(`  - ${orphan.id} (${orphan.component}) - Running for ${Math.round(orphan.age / 1000)}s`);
      });
    }

    return orphans;
  }
}

// Create global instance
const pollingRegistry = new PollingRegistry();

// Expose globally for debugging
window.pollingRegistry = pollingRegistry;
window.logActivePolling = () => pollingRegistry.logActive();
window.cleanupAllPolling = () => pollingRegistry.cleanupAll();
window.checkOrphanedPolling = () => pollingRegistry.checkForOrphans();

export default pollingRegistry;
